// Health comparator GTM tracking — 1:1 port of the legacy Vue store
// `reassurezmoi/assets/js/stores/common/tracking_module.js`. Pushes the exact
// same dataLayer events (identical names/payloads) the Vue comparator fired so
// existing GTM tags/triggers keep working unchanged. Deliberately bypasses the
// app's `useTracking().track()` context: that mirrors every event to
// `/api/events/track`, which the legacy comparator never did (and which would
// be meaningless for the `event: 'event'` GA micro-events below).
//
// Keep in sync with tracking_module.js.

import { hashEmail } from "@/app/utils/security-utils"
import { getCookie } from "@/app/utils/cookie-utils"
import { CookiesEnum } from "@/app/enums/Cookies"

// ttclid/ttp lus côté client : l'appel Events API TikTok est cross-origin, les cookies ne sont pas transmis.
export const tiktokClickIdentity = (): { ttclid?: string; ttp?: string } => ({
    ttclid: getCookie(CookiesEnum.USER_TTCLID) ?? undefined,
    ttp: getCookie("_ttp") ?? undefined,
})

const pushDataLayer = (payload: Record<string, unknown>) => {
    if (typeof window === "undefined") return
    if (!window.dataLayer) window.dataLayer = []
    window.dataLayer.push(payload)
}

// tracking/triggerEvent — raw named GTM event.
export const triggerEvent = (
    eventName: string,
    data: Record<string, unknown> = {},
) => {
    pushDataLayer({ event: eventName, ...data })
}

// tracking/gtag — GA event routed through GTM (event: 'event').
export const gtag = (
    eventAction: string,
    eventCategory: string,
    eventLabel: string,
) => {
    pushDataLayer({ event: "event", eventAction, eventCategory, eventLabel })
}

// Fire `eventName` with an SHA-256 `email_hash` when hashing succeeds, falling
// back to the bare event otherwise — mirrors the legacy try/catch pattern used
// by trackConversion / trackGadsHotLeadConversion / trackGadsColdLeadConversion.
export const triggerEventWithEmailHash = async (
    eventName: string,
    email: string | null | undefined,
    data: Record<string, unknown> = {},
) => {
    if (!email) {
        triggerEvent(eventName, data)
        return
    }
    try {
        const email_hash = await hashEmail(email)
        triggerEvent(eventName, { ...data, email_hash })
    } catch {
        triggerEvent(eventName, data)
    }
}

export interface FacebookConversionData {
    event_name: string
    user_data: Record<string, unknown>
    custom_data: Record<string, unknown>
}

// tracking/facebookConversionTrack — fire-and-forget POST to the Symfony
// Conversions API endpoint (matches the legacy axios.post call).
export const facebookConversionTrack = (data: FacebookConversionData) => {
    const base = process.env.NEXT_PUBLIC_RM_BASE_API ?? ""
    void fetch(`${base}/api/facebook/conversion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
    }).catch(() => {
        // best-effort; tracking failures must never break the funnel
    })
}

export interface TikTokConversionData {
    event_name: string
    user_data: Record<string, unknown>
    custom_data: Record<string, unknown>
    // Partagé avec le pixel TikTok (GTM) pour la déduplication pixel ↔ Events API serveur.
    event_id?: string
}

// tracking/tiktokConversionTrack — miroir de facebookConversionTrack vers l'Events API TikTok
// (endpoint Symfony /api/tiktok/conversion). Fire-and-forget : ne doit jamais casser le tunnel.
export const tiktokConversionTrack = (data: TikTokConversionData) => {
    const base = process.env.NEXT_PUBLIC_RM_BASE_API ?? ""
    void fetch(`${base}/api/tiktok/conversion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
    }).catch(() => {
        // best-effort; tracking failures must never break the funnel
    })
}

// event_id partagé pixel ↔ Events API pour la déduplication.
export const createEventId = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID()
    }
    return `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

// Valeur de conversion (commission RM estimée) coupée par défaut : la plomberie de tracking part en prod
// sans exposer notre marge aux régies. Passer NEXT_PUBLIC_SEND_CONVERSION_VALUE=true pour l'activer.
export const conversionValueEnabled = (): boolean =>
    process.env.NEXT_PUBLIC_SEND_CONVERSION_VALUE === "true"

// Clic sur une offre → event `ClickOffer` avec la commission estimée. `product` = loan/health/travel
// (content_category) pour réutiliser le même point d'entrée sur les 3 comparateurs.
export const tiktokOfferClick = (params: {
    product: string
    offerId: string
    value?: number
    token?: string | null
    email?: string | null
}) => {
    tiktokConversionTrack({
        event_name: "ClickOffer",
        user_data: { email: params.email ?? "", ...tiktokClickIdentity() },
        custom_data: {
            token: params.token ?? "",
            content_category: params.product,
            content_ids: [params.offerId],
            content_name: params.offerId,
            ...(conversionValueEnabled() && params.value != null
                ? { value: params.value, currency: "EUR" }
                : {}),
        },
        event_id: createEventId(),
    })
}
